import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { hasCapability, isAdminRole, formatINR, formatDate, formatTime, formatDateTime } from "@/lib/permissions";
import {
  ArrowLeft, CheckCircle2, XCircle, PackagePlus, PackageCheck, Clock, ExternalLink, Trash2, User, Building2, Calendar, FileText,
} from "lucide-react";

const STATUS_STYLES = {
  pending: { icon: Clock, cls: "bg-amber-50 text-amber-800 border-amber-200", label: "Pending Approval" },
  approved: { icon: CheckCircle2, cls: "bg-sky-50 text-sky-800 border-sky-200", label: "Approved" },
  rejected: { icon: XCircle, cls: "bg-rose-50 text-rose-800 border-rose-200", label: "Rejected" },
  po_raised: { icon: PackagePlus, cls: "bg-indigo-50 text-indigo-800 border-indigo-200", label: "PO Raised" },
  partially_fulfilled: { icon: PackageCheck, cls: "bg-teal-50 text-teal-800 border-teal-200", label: "Partially Fulfilled" },
  fulfilled: { icon: PackageCheck, cls: "bg-emerald-50 text-emerald-800 border-emerald-200", label: "Fulfilled" },
  cancelled: { icon: XCircle, cls: "bg-slate-100 text-slate-600 border-slate-200", label: "Cancelled" },
};

export default function ConsumableRequestDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [req, setReq] = useState(null);
  const [loading, setLoading] = useState(true);
  const [dialog, setDialog] = useState(null); // 'approve' | 'reject' | null
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);

  const canApprove = hasCapability(user, "approve_consumable_request");
  const canRaisePo = hasCapability(user, "raise_po");
  const isRequester = req && user && req.requested_by === user.id;

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get(`/consumable-requests/${id}`);
      setReq(data);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Failed to load requisition");
      navigate("/consumable-requests");
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [id]);

  const doAction = async (action) => {
    if (action === "reject" && !note.trim()) {
      toast.error("Please provide a rejection reason");
      return;
    }
    setBusy(true);
    try {
      await api.post(`/consumable-requests/${id}/${action}`, { note, reason: note });
      toast.success(action === "approve" ? "Requisition approved — you can now raise a PO" : "Requisition rejected");
      setDialog(null);
      setNote("");
      load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || `Failed to ${action}`);
    } finally {
      setBusy(false);
    }
  };

  const cancel = async () => {
    if (!window.confirm("Cancel this requisition? This cannot be undone.")) return;
    setBusy(true);
    try {
      await api.post(`/consumable-requests/${id}/cancel`);
      toast.success("Requisition cancelled");
      load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Failed to cancel");
    } finally {
      setBusy(false);
    }
  };

  const raisePo = () => {
    // Pre-fill the PO form via query param — PurchaseOrderForm reads it on load
    navigate(`/purchase-orders/new?from_request=${id}`);
  };

  if (loading || !req) {
    return <div className="p-12 text-center text-slate-400">Loading…</div>;
  }

  const st = STATUS_STYLES[req.status] || STATUS_STYLES.pending;
  const StIcon = st.icon;
  const totalRequested = (req.items || []).reduce((s, i) => s + (Number(i.qty_requested) || 0) * (Number(i.unit_cost_at_request) || 0), 0);
  const totalReceived = (req.items || []).reduce((s, i) => s + (Number(i.qty_received) || 0) * (Number(i.unit_cost_at_request) || 0), 0);

  return (
    <div className="max-w-5xl mx-auto space-y-6" data-testid="request-detail-page">
      <button
        onClick={() => navigate("/consumable-requests")}
        className="text-sm text-slate-500 hover:text-slate-900 flex items-center gap-1"
      >
        <ArrowLeft className="w-4 h-4" /> Back to Requisitions
      </button>

      <div className="flex flex-wrap items-start justify-between gap-4">
        <div>
          <div className="text-xs font-semibold tracking-widest uppercase text-[#7C1D3A]">Requisition</div>
          <h1 className="font-heading text-3xl font-bold text-slate-900 mt-1 font-mono">{req.request_number}</h1>
          <div className="mt-2">
            <Badge className={`gap-1 border ${st.cls}`} data-testid="request-status-badge">
              <StIcon className="w-3.5 h-3.5" /> {st.label}
            </Badge>
            <span className={`ml-2 inline-block px-2 py-0.5 rounded-full text-[10px] font-semibold uppercase ${req.priority === "urgent" ? "bg-rose-100 text-rose-800" : req.priority === "high" ? "bg-amber-100 text-amber-800" : "bg-slate-100 text-slate-600"}`}>
              {req.priority} priority
            </span>
          </div>
        </div>

        {/* Actions */}
        <div className="flex flex-wrap gap-2">
          {req.status === "pending" && canApprove && (
            <>
              <Button
                onClick={() => { setNote(""); setDialog("approve"); }}
                className="bg-emerald-600 hover:bg-emerald-700"
                data-testid="approve-request-btn"
              >
                <CheckCircle2 className="w-4 h-4 mr-2" /> Approve
              </Button>
              <Button
                onClick={() => { setNote(""); setDialog("reject"); }}
                variant="outline"
                className="border-rose-300 text-rose-700 hover:bg-rose-50"
                data-testid="reject-request-btn"
              >
                <XCircle className="w-4 h-4 mr-2" /> Reject
              </Button>
            </>
          )}
          {req.status === "approved" && canRaisePo && (
            <Button
              onClick={raisePo}
              className="bg-[#7C1D3A] hover:bg-[#6c1c36]"
              data-testid="raise-po-from-request-btn"
            >
              <PackagePlus className="w-4 h-4 mr-2" /> Raise PO
            </Button>
          )}
          {(req.status === "po_raised" || req.status === "partially_fulfilled" || req.status === "fulfilled") && req.po_id && (
            <Button
              onClick={() => navigate(`/purchase-orders/${req.po_id}`)}
              variant="outline"
              data-testid="view-linked-po-btn"
            >
              <ExternalLink className="w-4 h-4 mr-2" /> View PO {req.po_number}
            </Button>
          )}
          {req.status === "pending" && (isRequester || isAdminRole(user?.role)) && (
            <Button onClick={cancel} variant="outline" size="sm" className="text-slate-500" disabled={busy}>
              <Trash2 className="w-4 h-4 mr-1" /> Cancel
            </Button>
          )}
        </div>
      </div>

      {/* Rejection reason banner */}
      {req.status === "rejected" && req.rejection_reason && (
        <Card className="bg-rose-50 border-rose-200">
          <CardContent className="p-4 flex gap-3">
            <XCircle className="w-5 h-5 text-rose-600 mt-0.5" />
            <div>
              <div className="text-sm font-semibold text-rose-800">Rejected by {req.approved_by_name}</div>
              <div className="text-sm text-rose-700 mt-1">{req.rejection_reason}</div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Metadata grid */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <Meta icon={<User className="w-3.5 h-3.5" />} label="Requester" value={req.requested_by_name} sub={req.requested_by_email} />
        <Meta icon={<Building2 className="w-3.5 h-3.5" />} label="Department" value={req.department || "—"} />
        <Meta icon={<Calendar className="w-3.5 h-3.5" />} label="Raised" value={formatDate(req.created_at)} sub={formatTime(req.created_at)} />
        <Meta icon={<Calendar className="w-3.5 h-3.5" />} label="Needed by" value={req.needed_by || "—"} />
      </div>

      {/* Justification */}
      {req.justification && (
        <Card>
          <CardContent className="p-4">
            <div className="text-[10px] uppercase font-semibold text-slate-500 tracking-wider mb-1 flex items-center gap-1">
              <FileText className="w-3 h-3" /> Justification
            </div>
            <div className="text-sm text-slate-800">{req.justification}</div>
          </CardContent>
        </Card>
      )}

      {/* Items */}
      <Card>
        <CardHeader className="pb-2"><CardTitle className="text-base font-heading">Items</CardTitle></CardHeader>
        <CardContent className="p-0">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-slate-50 border-b border-slate-200">
                <tr className="text-slate-500 font-semibold text-[11px] uppercase tracking-wider">
                  <th className="px-4 py-3 text-left">Code / Name</th>
                  <th className="px-4 py-3 text-right">Requested</th>
                  <th className="px-4 py-3 text-right">Received</th>
                  <th className="px-4 py-3 text-right">Unit Cost</th>
                  <th className="px-4 py-3 text-right">Est. Value</th>
                  <th className="px-4 py-3 text-left">Note</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {(req.items || []).map((it) => {
                  const recv = Number(it.qty_received) || 0;
                  const req_qty = Number(it.qty_requested) || 0;
                  const done = recv >= req_qty;
                  return (
                    <tr key={it.id} className="hover:bg-slate-50">
                      <td className="px-4 py-3">
                        <div className="font-mono text-xs text-[#7C1D3A] font-semibold">{it.code}</div>
                        <div className="text-slate-900">{it.name}</div>
                        <div className="text-[11px] text-slate-500">{it.category} · stock at request: {it.stock_at_request} {it.unit_of_measure}</div>
                      </td>
                      <td className="px-4 py-3 text-right font-mono">{req_qty} {it.unit_of_measure}</td>
                      <td className={`px-4 py-3 text-right font-mono ${done ? "text-emerald-700 font-semibold" : recv > 0 ? "text-teal-700" : "text-slate-400"}`}>
                        {recv} {it.unit_of_measure}
                      </td>
                      <td className="px-4 py-3 text-right font-mono text-slate-600">{formatINR(it.unit_cost_at_request)}</td>
                      <td className="px-4 py-3 text-right font-mono">{formatINR(req_qty * it.unit_cost_at_request)}</td>
                      <td className="px-4 py-3 text-xs text-slate-600">{it.note || "—"}</td>
                    </tr>
                  );
                })}
              </tbody>
              <tfoot className="bg-slate-50 border-t-2 border-slate-200">
                <tr className="font-semibold text-sm">
                  <td className="px-4 py-3">Total</td>
                  <td colSpan={3} />
                  <td className="px-4 py-3 text-right font-mono text-[#7C1D3A]">{formatINR(totalRequested)}</td>
                  <td className="px-4 py-3 text-xs text-slate-500">
                    {totalReceived > 0 && `Received value: ${formatINR(totalReceived)}`}
                  </td>
                </tr>
              </tfoot>
            </table>
          </div>
        </CardContent>
      </Card>

      {/* Timeline */}
      <Card>
        <CardHeader className="pb-2"><CardTitle className="text-base font-heading">Timeline</CardTitle></CardHeader>
        <CardContent className="p-4 space-y-2">
          <TimelineItem when={req.created_at} who={req.requested_by_name} action="Raised requisition" />
          {req.approved_at && req.status !== "rejected" && (
            <TimelineItem when={req.approved_at} who={req.approved_by_name} action="Approved" note={req.approval_note} />
          )}
          {req.approved_at && req.status === "rejected" && (
            <TimelineItem when={req.approved_at} who={req.approved_by_name} action="Rejected" note={req.rejection_reason} tone="rose" />
          )}
          {req.po_raised_at && (
            <TimelineItem when={req.po_raised_at} action={`PO ${req.po_number} raised`} tone="indigo" />
          )}
          {req.fulfilled_at && (
            <TimelineItem when={req.fulfilled_at} action="Fully received — stock & ledger updated" tone="emerald" />
          )}
        </CardContent>
      </Card>

      {/* Approve / Reject dialog */}
      <Dialog open={!!dialog} onOpenChange={(v) => !v && setDialog(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{dialog === "approve" ? "Approve Requisition" : "Reject Requisition"}</DialogTitle>
          </DialogHeader>
          <div className="space-y-2">
            <label className="text-sm font-medium">
              {dialog === "approve" ? "Approval note (optional)" : "Rejection reason (required)"}
            </label>
            <Textarea
              value={note}
              onChange={(e) => setNote(e.target.value)}
              placeholder={dialog === "approve" ? "e.g. Vendor: Blue Star. Please raise PO." : "Why is this being rejected?"}
              rows={4}
              data-testid={`${dialog}-note`}
            />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDialog(null)}>Cancel</Button>
            <Button
              onClick={() => doAction(dialog)}
              disabled={busy}
              className={dialog === "approve" ? "bg-emerald-600 hover:bg-emerald-700" : "bg-rose-600 hover:bg-rose-700"}
              data-testid={`confirm-${dialog}-btn`}
            >
              {busy ? "Working…" : dialog === "approve" ? "Approve" : "Reject"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function Meta({ icon, label, value, sub }) {
  return (
    <Card><CardContent className="p-3">
      <div className="text-[10px] uppercase text-slate-500 flex items-center gap-1 tracking-wider">{icon}{label}</div>
      <div className="text-sm font-semibold text-slate-900 mt-1 truncate">{value}</div>
      {sub && <div className="text-[11px] text-slate-500 truncate">{sub}</div>}
    </CardContent></Card>
  );
}

function TimelineItem({ when, who, action, note, tone = "slate" }) {
  const tones = {
    slate: "bg-slate-400",
    emerald: "bg-emerald-500",
    rose: "bg-rose-500",
    indigo: "bg-indigo-500",
  };
  const d = new Date(when);
  return (
    <div className="flex gap-3 text-sm">
      <div className={`w-2 h-2 rounded-full mt-1.5 ${tones[tone]}`} />
      <div className="flex-1">
        <div>
          <span className="font-medium text-slate-900">{action}</span>
          {who && <span className="text-slate-500"> · {who}</span>}
        </div>
        {note && <div className="text-xs text-slate-500 italic">&ldquo;{note}&rdquo;</div>}
        <div className="text-[11px] text-slate-400">{formatDateTime(d)}</div>
      </div>
    </div>
  );
}
